import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import confetti from 'canvas-confetti';
import { Loader2 } from 'lucide-react';
import {
  buildDueQueue,
  rateCard,
  parseFlashcardConfig,
  fetchCardReviewInsights,
  type FlashcardQueueItem,
  type Rating,
  type FlashcardModuleConfig,
  type FlashcardCardInsights,
  type FlashcardStudyScope,
} from '../../services/flashcardService';
import { ratingButtonHints } from '../../utils/flashcardInsights';
import FlashcardCardInsightsPanel from './FlashcardCardInsights';
import type { Tutee } from '../../types/tuition';

interface FlashcardReviewProps {
  tutee: Tutee;
  studentSlug: string;
  moduleConfig: FlashcardModuleConfig;
  studyScope?: FlashcardStudyScope;
  onSessionEnd: (reviewed: number) => void;
  onEmpty: () => void;
}

const RATING_BUTTON_STYLES: Record<Rating, { label: string; className: string; key: string }> = {
  again: { label: 'Again', className: 'bg-red-500 hover:bg-red-600 text-white', key: '1' },
  hard: { label: 'Hard', className: 'bg-orange-500 hover:bg-orange-600 text-white', key: '2' },
  good: { label: 'Good', className: 'bg-green-500 hover:bg-green-600 text-white', key: '3' },
  easy: { label: 'Easy', className: 'bg-blue-500 hover:bg-blue-600 text-white', key: '4' },
};

function shuffleArray<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

const FlashcardReview = ({
  tutee,
  studentSlug,
  moduleConfig,
  studyScope = 'all',
  onSessionEnd,
  onEmpty,
}: FlashcardReviewProps) => {
  const [queue, setQueue] = useState<FlashcardQueueItem[]>([]);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  /** Enables 3D flip transition only after user interaction (not when a new card mounts). */
  const [flipAnimEnabled, setFlipAnimEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(false);
  const [shake, setShake] = useState(false);
  const [reviewedCount, setReviewedCount] = useState(0);
  const [hasRevealedBack, setHasRevealedBack] = useState(false);
  const [cardInsights, setCardInsights] = useState<FlashcardCardInsights | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const flipAtRef = useRef<number>(Date.now());
  const reducedMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const loadQueue = useCallback(
    async (opts?: { shuffle?: boolean; keepPosition?: boolean }) => {
      setLoading(true);
      try {
        let items = await buildDueQueue(tutee.id, studentSlug, moduleConfig, studyScope);
        if (opts?.shuffle) items = shuffleArray(items);
        setQueue(items);
        if (!opts?.keepPosition) {
          setIndex(0);
          setFlipped(false);
          setFlipAnimEnabled(false);
          setHasRevealedBack(false);
        }
        if (items.length === 0) onEmpty();
      } catch (e) {
        console.error(e);
        setQueue([]);
        onEmpty();
      } finally {
        setLoading(false);
      }
    },
    [tutee.id, studentSlug, moduleConfig, studyScope, onEmpty]
  );

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const current = queue[index];
  const sm2Config = useMemo(() => parseFlashcardConfig(moduleConfig), [moduleConfig]);
  const ratingHints = useMemo(
    () => ratingButtonHints(current?.progress ?? null, sm2Config),
    [current?.progress, sm2Config]
  );
  useEffect(() => {
    setFlipped(false);
    setFlipAnimEnabled(false);
    setHasRevealedBack(false);
    flipAtRef.current = Date.now();
  }, [current?.card.id]);

  useEffect(() => {
    if (!current) {
      setCardInsights(null);
      return;
    }
    setCardInsights(null);
    let cancelled = false;
    setInsightsLoading(true);
    fetchCardReviewInsights(tutee.id, studentSlug, current.card.id, current.progress)
      .then((data) => {
        if (!cancelled) {
          setCardInsights(data);
          setInsightsLoading(false);
        }
      })
      .catch((e) => {
        console.error(e);
        if (!cancelled) {
          setCardInsights(null);
          setInsightsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [current?.card.id, tutee.id, studentSlug]);

  const toggleFlip = useCallback(() => {
    setFlipAnimEnabled(true);
    setFlipped((wasFlipped) => {
      if (!wasFlipped) {
        setHasRevealedBack(true);
        flipAtRef.current = Date.now();
      }
      return !wasFlipped;
    });
  }, []);

  const advanceToNextCard = useCallback(() => {
    setIndex((i) => i + 1);
  }, []);

  const handleRate = useCallback(
    async (r: Rating) => {
      const item = queue[index];
      if (!item || rating || !hasRevealedBack) return;
      setRating(true);
      const durationMs = Date.now() - flipAtRef.current;

      try {
        await rateCard({
          tuteeId: tutee.id,
          studentSlug,
          card: item.card,
          progress: item.progress,
          rating: r,
          config: sm2Config,
          durationMs,
        });

        if (r === 'again') {
          setShake(true);
          setTimeout(() => setShake(false), 500);
        }
        if (r === 'easy') {
          confetti({ particleCount: 40, spread: 50, origin: { y: 0.7 } });
        }

        const nextReviewed = reviewedCount + 1;
        setReviewedCount(nextReviewed);

        if (index + 1 >= queue.length) {
          confetti({ particleCount: 120, spread: 70, origin: { y: 0.6 } });
          onSessionEnd(nextReviewed);
          return;
        }

        advanceToNextCard();
      } catch (e) {
        console.error(e);
      } finally {
        setRating(false);
      }
    },
    [
      queue,
      index,
      rating,
      hasRevealedBack,
      tutee.id,
      studentSlug,
      sm2Config,
      reviewedCount,
      onSessionEnd,
      advanceToNextCard,
    ]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!current || rating) return;
      if (e.code === 'Space') {
        e.preventDefault();
        toggleFlip();
        return;
      }
      if (hasRevealedBack) {
        const map: Record<string, Rating> = { '1': 'again', '2': 'hard', '3': 'good', '4': 'easy' };
        if (map[e.key]) {
          e.preventDefault();
          handleRate(map[e.key]);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current, rating, hasRevealedBack, handleRate, toggleFlip]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <Loader2 className="w-12 h-12 animate-spin text-indigo-500" />
        <p className="mt-4 text-gray-600 font-medium">Loading cards…</p>
      </div>
    );
  }

  if (!current) {
    return null;
  }

  const use3DFlip = !reducedMotion;
  const showRatings = hasRevealedBack;

  return (
    <div className="max-w-lg mx-auto w-full px-4">
      <p className="text-center text-sm font-bold text-gray-500 mb-4">
        {index + 1} / {queue.length}
      </p>

      <div
        key={current.card.id}
        className={`w-full mb-6 ${shake ? 'animate-shake' : ''}`}
      >
        {use3DFlip ? (
          <div className="perspective-[1200px] w-full min-h-[220px] sm:min-h-[280px]">
            <button
              type="button"
              onClick={toggleFlip}
              className={`relative w-full min-h-[220px] sm:min-h-[280px] preserve-3d cursor-pointer ${
                flipAnimEnabled ? 'transition-transform duration-500 ease-in-out' : ''
              } ${flipped ? '[transform:rotateY(180deg)]' : ''}`}
              style={{ transformStyle: 'preserve-3d' }}
            >
              <div
                className="absolute inset-0 backface-hidden rounded-2xl shadow-xl p-6 sm:p-8 flex flex-col items-center justify-center text-center bg-white border-2 border-indigo-100"
                style={{ backfaceVisibility: 'hidden' }}
              >
                <p className="text-xl sm:text-2xl font-bold text-gray-900 whitespace-pre-wrap">
                  {current.card.front}
                </p>
                {!flipped && (
                  <span className="absolute bottom-4 text-xs font-bold text-indigo-400 uppercase tracking-wide">
                    Tap to reveal answer
                  </span>
                )}
              </div>
              <div
                className="absolute inset-0 backface-hidden rounded-2xl shadow-xl p-6 sm:p-8 flex flex-col items-center justify-center text-center bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 [transform:rotateY(180deg)]"
                style={{ backfaceVisibility: 'hidden' }}
              >
                <p className="text-lg sm:text-xl font-semibold text-gray-800 whitespace-pre-wrap">
                  {current.card.back}
                </p>
                {flipped && (
                  <span className="absolute bottom-4 text-xs font-bold text-indigo-500 uppercase tracking-wide">
                    Tap to see question again
                  </span>
                )}
              </div>
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={toggleFlip}
            className="relative w-full min-h-[220px] sm:min-h-[280px] cursor-pointer"
          >
            <div
              className={`absolute inset-0 rounded-2xl shadow-xl p-6 sm:p-8 flex flex-col items-center justify-center text-center bg-white border-2 border-indigo-100 transition-opacity duration-300 ${
                flipped ? 'opacity-0 pointer-events-none' : 'opacity-100'
              }`}
            >
              <p className="text-xl sm:text-2xl font-bold text-gray-900 whitespace-pre-wrap">
                {current.card.front}
              </p>
              {!flipped && (
                <span className="absolute bottom-4 text-xs font-bold text-indigo-400 uppercase tracking-wide">
                  Tap to reveal answer
                </span>
              )}
            </div>
            <div
              className={`absolute inset-0 rounded-2xl shadow-xl p-6 sm:p-8 flex flex-col items-center justify-center text-center bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 transition-opacity duration-300 ${
                flipped ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              <p className="text-lg sm:text-xl font-semibold text-gray-800 whitespace-pre-wrap">
                {current.card.back}
              </p>
              {flipped && (
                <span className="absolute bottom-4 text-xs font-bold text-indigo-500 uppercase tracking-wide">
                  Tap to see question again
                </span>
              )}
            </div>
          </button>
        )}
      </div>

      <FlashcardCardInsightsPanel
        insights={cardInsights}
        loading={insightsLoading}
        className={showRatings ? 'mb-8' : ''}
      />

      {showRatings && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          {(Object.keys(RATING_BUTTON_STYLES) as Rating[]).map((ratingKey) => {
            const btn = RATING_BUTTON_STYLES[ratingKey];
            const sub = ratingHints[ratingKey];
            return (
              <button
                key={ratingKey}
                type="button"
                disabled={rating}
                onClick={() => handleRate(ratingKey)}
                className={`py-4 px-2 rounded-xl font-bold text-sm sm:text-base active:scale-95 transition-all disabled:opacity-50 ${btn.className}`}
              >
                {btn.label}
                {sub && <span className="block text-[10px] opacity-80">{sub}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FlashcardReview;
